import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { toCsv } from "./billing-model";

const BOM = String.fromCharCode(0xfeff);

function download(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([BOM, csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** "Export usage": daily + per-agent + per-channel credits of this cycle as one CSV (built client-side). */
export function useExportUsage() {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  return useMutation({
    mutationFn: async () => {
      const groups = await Promise.all(
        (["day", "agent", "channel"] as const).map(async (by) => ({ by, rows: (await unwrap(api.GET("/v1/billing/usage/breakdown", { params: { query: { by } } }))).rows })),
      );
      const header = [t("billing.export.group"), t("billing.export.key"), t("billing.breakdown.answers"), t("billing.breakdown.credits"), t("billing.breakdown.share")];
      const body = groups.flatMap((group) => group.rows.map((row) => [group.by, row.label, row.answers, row.credits, row.share_pct]));
      const filename = `${summary.slug}-usage-${new Date().toISOString().slice(0, 7)}.csv`;
      download(filename, toCsv([header, ...body]));
      return filename;
    },
    onSuccess: (filename) => toast.success(t("billing.export.done", { file: filename })),
    onError: (caught) => toast.error(message(caught)),
  });
}
